import React, { useEffect, useContext } from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import clsx from "clsx";
import firebase from "../../firebase";
import { SnackbarAlert } from "../SnackbarAlert";

import { makeStyles, withStyles } from "@material-ui/core/styles";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import Tooltip from "@material-ui/core/Tooltip";
import IconButton from "@material-ui/core/IconButton";
import CancelRoundedIcon from "@material-ui/icons/CancelRounded";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";
import InputAdornment from "@material-ui/core/InputAdornment";
import DialogActions from "@material-ui/core/DialogActions";

const useStyles = makeStyles((theme) => ({
  input: {
    margin: 8,
  },
  button: {
    color: theme.palette.primary.main,
    textTransform: "none",
    padding: theme.spacing(1),
    borderRadius: 8,
    fontSize: "1.3em",
    fontWeight: 600,
    letterSpacing: 1,
  },
  dialog: {
    padding: theme.spacing(1),
    width: "auto",
    color: theme.palette.common.white,
  },
  dialogTitle: {
    background: theme.palette.primary.main,
  },
  dialogActions: {
    background: "hsl(0, 0%, 97%)",
  },
}));

const styles = (theme) => ({
  closeButton: {
    position: "absolute",
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[50],
    fontSize: 40,
  },
});

const DialogTitle = withStyles(styles)((props) => {
  const { children, action, className, classes, ...other } = props;
  return (
    <MuiDialogTitle disableTypography {...other} className={className}>
      <Typography component="div">
        <Box
          fontSize={24}
          fontWeight="fontWeightBold"
          letterSpacing={0.7}
          p={1}
        >
          {children}
        </Box>
      </Typography>
      {action ? (
        <span>
          <Tooltip
            title="Close"
            enterDelay={300}
            leaveDelay={200}
            enterNextDelay={300}
          >
            <IconButton
              aria-label="close"
              className={classes.closeButton}
              onClick={action}
            >
              <CancelRoundedIcon />
            </IconButton>
          </Tooltip>
        </span>
      ) : null}
    </MuiDialogTitle>
  );
});

const EditProfileForm = (props) => {
  const classes = useStyles();
  const updateProfile = firebase.functions().httpsCallable("updateProfile");
  const { setSnackbarState } = useContext(SnackbarAlert);

  return (
    <Formik
      initialValues={{
        firstName: props.firstName,
        lastName: props.lastName,
        email: props.email,
        mobileNumber: props.phoneNumber.substring(3),
        country: props.country,
        specialty: props.specialty ? props.specialty : "",
      }}
      validationSchema={Yup.object().shape({
        firstName: Yup.string().required("Please input your first name"),
        lastName: Yup.string().required("Please input your last name"),
        email: Yup.string()
          .required("Please input your email")
          .email("Invalid format"),
        mobileNumber: Yup.number()
          .max(9999999999, "Invalid mobile number")
          .required("Please input your mobile number"),
        specialty: props.specialty
          ? Yup.string().required("Please input your specialty")
          : Yup.string(),
        country: Yup.string(),
      })}
      onSubmit={async (values, actions) => {
        //submit
        try {
          const user = await firebase.auth().currentUser;

          await user.updateProfile({
            email: values.email,
            phoneNumber: "+63 " + values.mobileNumber,
            displayName: values.firstName + " " + values.lastName,
          });
          await firebase
            .database()
            .ref("users")
            .child(props.id)
            .set({
              certificateUrl: props.isVerified ? props.certificateUrl : null,
              country: values.country,
              email: values.email,
              firstName: values.firstName,
              fullName: values.firstName + " " + values.lastName,
              isVerified: props.isVerified ? props.isVerified : null,
              lastName: values.lastName,
              phoneNumber: "+63 " + values.mobileNumber,
              specialty: props.isVerified ? values.specialty : null,
            });
          actions.resetForm();
          props.close();
          setSnackbarState({
            state: true,
            message: "Profile successfully updated! 👍",
            status: "info",
          });
        } catch (error) {
          //console.log(error);
          props.close();
          setSnackbarState({
            state: true,
            message: "Something went wrong. 😟",
            status: "error",
          });
        }
      }}
    >
      {(formik) => (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            formik.handleSubmit();
          }}
          noValidate
          autoComplete="off"
        >
          <DialogTitle
            action={props.close}
            className={clsx(classes.dialog, classes.dialogTitle)}
          >
            Edit Profile
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={1}>
              <Grid item lg={12}>
                <TextField
                  id="firstName"
                  name="firstName"
                  placeholder="First Name"
                  margin="dense"
                  className={classes.input}
                  {...formik.getFieldProps("firstName")}
                  error={formik.errors.firstName && formik.touched.firstName}
                  helperText={
                    formik.errors.firstName && formik.touched.firstName
                      ? formik.errors.firstName
                      : "First Name"
                  }
                  disabled={formik.isSubmitting}
                  fullWidth
                />
              </Grid>
              <Grid item lg={12}>
                <TextField
                  id="lastName"
                  name="lastName"
                  placeholder="Last Name"
                  margin="dense"
                  className={classes.input}
                  {...formik.getFieldProps("lastName")}
                  error={formik.errors.lastName && formik.touched.lastName}
                  helperText={
                    formik.errors.lastName && formik.touched.lastName
                      ? formik.errors.lastName
                      : "Last Name"
                  }
                  disabled={formik.isSubmitting}
                  fullWidth
                />
              </Grid>
              <Grid item lg={12}>
                <TextField
                  id="email"
                  name="email"
                  placeholder="Email ID"
                  margin="dense"
                  className={classes.input}
                  {...formik.getFieldProps("email")}
                  error={formik.errors.email && formik.touched.email}
                  helperText={
                    formik.errors.email && formik.touched.email
                      ? formik.errors.email
                      : "Email ID"
                  }
                  disabled={formik.isSubmitting}
                  fullWidth
                />
              </Grid>
              {props.isVerified ? (
                <Grid item lg={12}>
                  <TextField
                    id="specialty"
                    name="specialty"
                    placeholder="Specialty"
                    margin="dense"
                    className={classes.input}
                    {...formik.getFieldProps("specialty")}
                    error={formik.errors.email && formik.touched.email}
                    helperText={
                      formik.errors.email && formik.touched.email
                        ? formik.errors.email
                        : "Specialty"
                    }
                    disabled={formik.isSubmitting}
                    fullWidth
                  />
                </Grid>
              ) : null}
              <Grid item lg={12}>
                <TextField
                  id="country"
                  name="country"
                  placeholder="Country"
                  margin="dense"
                  className={classes.input}
                  defaultValue={formik.values.country}
                  helperText="Country"
                  disabled
                  fullWidth
                />
              </Grid>
              <Grid item lg={12}>
                <TextField
                  id="mobileNumber"
                  name="mobileNumber"
                  placeholder="Mobile Number"
                  margin="dense"
                  className={classes.input}
                  {...formik.getFieldProps("mobileNumber")}
                  error={
                    formik.errors.mobileNumber && formik.touched.mobileNumber
                  }
                  helperText={
                    formik.errors.mobileNumber && formik.touched.mobileNumber
                      ? formik.errors.mobileNumber
                      : "Mobile Number"
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">+63</InputAdornment>
                    ),
                  }}
                  disabled={formik.isSubmitting}
                  fullWidth
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions
            className={clsx(classes.dialog, classes.dialogActions)}
          >
            <Button
              variant="text"
              className={classes.button}
              disabled={formik.isSubmitting}
              type="submit"
              fullWidth
            >
              Save Changes
            </Button>
          </DialogActions>
        </form>
      )}
    </Formik>
  );
};

export default EditProfileForm;
